/**
 * AccountScreen — everything one signed-in player has done (stats phase).
 *
 * Drives the static #accountScreen shell the way LockerScreen drives its own:
 * caches ids, renders from one GET /api/account, and fails closed with a
 * status line plus Retry. Two columns per the spec: identity, rename and
 * personal records in the left rail; headline totals, the weapon table and the
 * mode table on the right.
 *
 * fetchFn is injected so this runs in jsdom with no network. The weapon table
 * is the shared renderer — the same one the end-of-match recap uses.
 */
import { renderWeaponTable } from './weaponTable.js';
import { renderAbilityTable } from './abilityTable.js';

const MAX_DISPLAY_NAME = 20;      // matches the server's 400 boundary

const TOTAL_TILES = [
  { key: 'kills', label: 'Kills' },
  { key: 'deaths', label: 'Deaths' },
  { key: 'damageDealt', label: 'Damage dealt' },
  { key: 'damageTaken', label: 'Damage taken' },
  { key: 'damageBlocked', label: 'Damage blocked' },
  { key: 'matchesPlayed', label: 'Matches' },
  { key: 'matchesWon', label: 'Wins' },
  { key: 'timePlayed', label: 'Time played', format: formatDuration }
];

const RECORD_ROWS = [
  { key: 'mostKillsInMatch', label: 'Most kills in a match' },
  { key: 'mostDamageInMatch', label: 'Most damage in a match' },
  { key: 'highestKillStreak', label: 'Highest kill streak' }
];

const MODE_COLUMNS = [
  { key: 'name', label: 'Mode' },
  { key: 'played', label: 'Played' },
  { key: 'won', label: 'Won' },
  { key: 'kills', label: 'Kills' },
  { key: 'deaths', label: 'Deaths' },
  { key: 'damageDealt', label: 'Damage' },
  { key: 'bestKills', label: 'Best' }
];

const AccountScreen = {
  els: {},
  deps: {},
  data: null,
  onKeyDown: null,
  saving: false,

  init(deps) {
    this.detachKeys();
    this.data = null;
    this.saving = false;
    this.deps = { fetchFn: (...a) => fetch(...a), showNotification: () => {}, onClose: () => {}, onDisplayNameChange: () => {}, ...deps };
    const byId = (id) => document.getElementById(id);
    this.els = {
      root: byId('accountScreen'), close: byId('accountClose'), status: byId('accountStatus'),
      avatar: byId('accountAvatar'), displayName: byId('accountDisplayName'), email: byId('accountEmail'),
      memberSince: byId('accountMemberSince'), nameInput: byId('accountNameInput'),
      nameSave: byId('accountNameSave'), nameError: byId('accountNameError'),
      records: byId('accountRecords'), ranked: byId('accountRanked'), totals: byId('accountTotals'),
      level: byId('accountLevel'), xpText: byId('accountXpText'), xpBar: byId('accountXpBar'),
      weapons: byId('accountWeapons'), abilities: byId('accountAbilities'), modes: byId('accountModes')
    };
    this.els.close?.addEventListener('click', () => this.hide());
    this.els.nameSave?.addEventListener('click', () => this.saveDisplayName());
    this.els.nameInput?.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.saveDisplayName(); });
  },

  isVisible() {
    return !!this.els.root && !this.els.root.classList.contains('hidden');
  },

  async show() {
    if (!this.els.root || this.isVisible()) return;   // re-opening would leak a second Escape listener
    this.els.root.classList.remove('hidden');
    this.onKeyDown = (e) => { if (e.key === 'Escape') this.hide(); };
    document.addEventListener('keydown', this.onKeyDown);
    await this.load();
  },

  hide() {
    if (!this.els.root) return;
    const wasVisible = this.isVisible();
    this.els.root.classList.add('hidden');
    this.detachKeys();
    if (wasVisible) this.deps.onClose();
  },

  /** Exactly one Escape listener can ever be attached: show() adds, hide()/init() take it away. */
  detachKeys() {
    if (this.onKeyDown) document.removeEventListener('keydown', this.onKeyDown);
    this.onKeyDown = null;
  },

  async load() {
    try {
      const res = await this.deps.fetchFn('/api/account');
      if (!res || !res.ok) throw new Error(`GET /api/account → ${res?.status}`);
      this.data = await res.json();
      this.clearStatus();
      this.render();
    } catch {
      this.data = null;
      this.renderStatus();
    }
  },

  clearStatus() {
    const { status } = this.els;
    if (!status) return;
    status.classList.add('hidden');
    status.innerHTML = '';
  },

  /** Fail closed, the way the locker does: say so and offer one Retry. */
  renderStatus() {
    const { status } = this.els;
    if (!status) return;
    status.classList.remove('hidden');
    status.innerHTML = '';
    const msg = document.createElement('span');
    msg.textContent = "Couldn't load your account stats.";
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Retry';
    retry.addEventListener('click', () => { this.load(); });
    status.append(msg, retry);
  },

  render() {
    if (!this.data) return;
    this.renderProfile();
    this.renderRanked();
    this.renderProgression();
    this.renderRecords();
    this.renderTotals();
    this.renderWeapons();
    this.renderAbilities();
    this.renderModes();
  },

  renderProfile() {
    const p = this.data.profile || {};
    const { avatar, displayName, email, memberSince, nameInput, nameError } = this.els;
    if (avatar) { if (p.avatarUrl) avatar.setAttribute('src', p.avatarUrl); else avatar.removeAttribute('src'); }
    if (displayName) displayName.textContent = p.displayName || '';
    if (email) email.textContent = p.email || '';
    if (memberSince) memberSince.textContent = p.createdAt ? `Member since ${formatDate(p.createdAt)}` : '';
    if (nameInput) nameInput.value = p.displayName || '';
    if (nameError) { nameError.textContent = ''; nameError.classList.remove('ok'); }
  },

  renderRanked() {
    const host = this.els.ranked;
    if (!host) return;
    host.innerHTML = '';
    const ranked = this.data.ranked;
    if (!ranked) {
      const empty = document.createElement('div');
      empty.className = 'stat-empty';
      empty.textContent = 'Play Ranked Battle Royale to get placed.';
      host.appendChild(empty);
      return;
    }
    if (ranked.provisionalRemaining > 0) {
      const line = document.createElement('div');
      line.className = 'stat-empty';
      line.textContent = `Placement matches left: ${ranked.provisionalRemaining}`;
      host.appendChild(line);
      return;
    }

    const name = document.createElement('div');
    name.className = 'ranked-tier-name';
    name.textContent = ranked.tier || '';
    const rp = document.createElement('div');
    rp.className = 'ranked-rp';
    rp.textContent = `${ranked.rp} RP`;
    host.append(name, rp);

    if (ranked.rpForTier) {
      const bar = document.createElement('div');
      bar.className = 'ranked-progress';
      bar.setAttribute('role', 'progressbar');
      const pct = Math.max(0, Math.min(100, (ranked.rpIntoTier / ranked.rpForTier) * 100));
      const fill = document.createElement('div');
      fill.className = 'ranked-progress-fill';
      fill.style.width = `${pct}%`;
      bar.appendChild(fill);
      const hint = document.createElement('div');
      hint.className = 'ranked-progress-label';
      hint.textContent = ranked.nextTier
        ? `${ranked.rpIntoTier} / ${ranked.rpForTier} to ${ranked.nextTier.name}`
        : '';
      host.append(bar, hint);
    }
  },

  renderProgression() {
    const { level, xpText, xpBar } = this.els;
    const p = this.data.progression || {};
    if (level) level.textContent = `Level ${p.level ?? 1}`;
    const into = Math.max(0, Math.round(p.xpIntoLevel || 0));
    if (p.xpForNextLevel == null) {                // at the level cap
      if (xpText) xpText.textContent = `${Math.round(p.xp || 0)} XP · MAX`;
      if (xpBar) xpBar.style.width = '100%';
      return;
    }
    const forNext = Math.round(p.xpForNextLevel);
    if (xpText) xpText.textContent = `${into} / ${forNext} XP`;
    const pct = forNext > 0 ? Math.min(100, Math.round((into / forNext) * 100)) : 0;
    if (xpBar) xpBar.style.width = `${pct}%`;
  },

  renderRecords() {
    const { records } = this.els;
    if (!records) return;
    records.innerHTML = '';
    const source = this.data.records || {};
    for (const row of RECORD_ROWS) {
      const line = document.createElement('div');
      line.className = 'stat-row';
      const label = document.createElement('span');
      label.className = 'label';
      label.textContent = row.label;
      const value = document.createElement('span');
      value.className = 'value';
      value.textContent = String(Math.round(source[row.key] || 0));
      line.append(label, value);
      records.appendChild(line);
    }
  },

  renderTotals() {
    const { totals } = this.els;
    if (!totals) return;
    totals.innerHTML = '';
    const source = this.data.totals || {};
    for (const tile of TOTAL_TILES) {
      const box = document.createElement('div');
      box.className = 'stat-tile';
      const value = document.createElement('div');
      value.className = 'value';
      const raw = source[tile.key] || 0;
      value.textContent = tile.format ? tile.format(raw) : String(Math.round(raw));
      const label = document.createElement('div');
      label.className = 'label';
      label.textContent = tile.label;
      box.append(value, label);
      totals.appendChild(box);
    }
  },

  renderWeapons() {
    const { weapons } = this.els;
    if (!weapons) return;
    weapons.innerHTML = '';
    weapons.appendChild(renderWeaponTable(this.data.weapons, { emptyMessage: 'No weapon damage recorded yet.' }));
  },

  renderAbilities() {
    const { abilities } = this.els;
    if (!abilities) return;
    abilities.innerHTML = '';
    abilities.appendChild(renderAbilityTable(this.data.abilities, { emptyMessage: 'No damage blocked yet.' }));
  },

  renderModes() {
    const { modes } = this.els;
    if (!modes) return;
    modes.innerHTML = '';
    const rows = Array.isArray(this.data.modes) ? this.data.modes : [];
    if (rows.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'stat-empty';
      empty.textContent = 'No matches played yet.';
      modes.appendChild(empty);
      return;
    }
    const table = document.createElement('table');
    table.className = 'stat-table mode-table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const col of MODE_COLUMNS) {
      const th = document.createElement('th');
      th.scope = 'col';
      th.textContent = col.label;
      headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    const tbody = document.createElement('tbody');
    for (const mode of rows) {
      const tr = document.createElement('tr');
      tr.dataset.gameMode = mode.gameMode || '';
      for (const col of MODE_COLUMNS) {
        const td = document.createElement('td');
        const raw = mode[col.key];
        td.textContent = col.key === 'name' ? String(raw || mode.gameMode || '') : String(Math.round(raw || 0));
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.append(thead, tbody);
    modes.appendChild(table);
  },

  /** Validate at the same boundary the server does, then PUT. */
  async saveDisplayName() {
    const { nameInput, nameSave, nameError, displayName } = this.els;
    if (!nameInput || this.saving) return;
    const next = nameInput.value.trim();
    if (nameError) nameError.classList.remove('ok');

    if (next.length === 0) { if (nameError) nameError.textContent = 'Enter a name.'; return; }
    if (next.length > MAX_DISPLAY_NAME) { if (nameError) nameError.textContent = `Use ${MAX_DISPLAY_NAME} characters or fewer.`; return; }

    this.saving = true;
    if (nameSave) nameSave.disabled = true;
    if (nameError) nameError.textContent = '';
    try {
      const res = await this.deps.fetchFn('/api/account/display-name', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ displayName: next })
      });
      if (!res || !res.ok) throw new Error(`PUT /api/account/display-name → ${res?.status}`);
      const body = await res.json();
      const saved = body?.displayName || next;
      if (displayName) displayName.textContent = saved;
      if (nameInput) nameInput.value = saved;
      // Replace rather than mutate: the parsed body is the caller's object.
      if (this.data) this.data = { ...this.data, profile: { ...this.data.profile, displayName: saved } };
      if (nameError) { nameError.textContent = 'Saved.'; nameError.classList.add('ok'); }
      this.deps.onDisplayNameChange(saved);
    } catch {
      if (nameError) nameError.textContent = "Couldn't save that name — try again.";
    } finally {
      this.saving = false;
      if (nameSave) nameSave.disabled = false;
    }
  }
};

/** Seconds → "12h 34m" / "34m" / "12s". timePlayed is seconds (statsService). */
function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${s}s`;
}

/** ISO-8601 → a short local date; the raw string if it will not parse. */
function formatDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export { AccountScreen, formatDuration, formatDate, MAX_DISPLAY_NAME };
